import CACA1080RequestModel from '@/api/data/CAC/CACA1080/CACA1080RequestModel'
import CACA1080ResponseModel from '@/api/data/CAC/CACA1080/CACA1080ResponseModel'
import CACG1080ViewModel from './CACG1080ViewModel'
import RestService from '@/common/RestService'
import ApiUrlConstants from '@/common/ApiUrlConstants'
import Moment from 'moment'
import { FormatConstants } from '@/common/FormatConstants'
import CommonUtil from '@/common/utils/CommonUtil'

const FIRST_DAY_OF_MONTH = 'YYYY-MM-01'
const DAY_OF_MONTH = 'YYYY-MM-DD'
const LAST_OF_MONTH = 'YYYY-MM-28'

export default class CACG1080Service {
  private first10YearDay: string
  private dayStart: number
  private dayEnd: number
  /**
   * CACA1080 API呼び出し
   * @param productNo 商品番号
   * @param shhnCd 当社商品コード
   * @returns APIからのレスポンスデータ
   */
  public async callAPICACA1080(
    productNo: string,
    shhnCd: string
  ): Promise<CACA1080ResponseModel> {
    const caca1080RequestModel = new CACA1080RequestModel()
    caca1080RequestModel.productNo = productNo
    caca1080RequestModel.shhnCd = shhnCd

    const caca1080ResponseModel: CACA1080ResponseModel = <
      CACA1080ResponseModel
    > await RestService.post(ApiUrlConstants.CACA1080, caca1080RequestModel)
    return caca1080ResponseModel
  }

  /**
   * 1年場合の折れ線チャートのデータフォーマット
   * @param cacg1080ViewModel「運用商品詳細（投資信託）：チャート」（CACG1080）画面のビューモデル
   * @returns 1年場合の折れ線チャート表示の返却データ
   */
  public formatDataChartOneYear(
    cacg1080ViewModel: CACG1080ViewModel
  ): Array<any> {
    const dataChartLineOneYearDaily = []
    const dataChartColumnOneYearDaily = []

    const oneDayBefore = Moment().subtract(1, 'day').format(DAY_OF_MONTH)
    const oneYearBefore = Moment().subtract(1, 'year').format(DAY_OF_MONTH)
    const oneDayBeforeTenYear = Moment().subtract(1, 'year').subtract(1, 'day').format(DAY_OF_MONTH)

    if (
      CommonUtil.isNotEmpty(cacg1080ViewModel.investTrustProductOneYearDailyLst)
    ) {
      const investTrustProductOneYear = cacg1080ViewModel.investTrustProductOneYearDailyLst.reverse()
      const startDate = investTrustProductOneYear[0].critValCritD
      const endDate = Moment(investTrustProductOneYear[investTrustProductOneYear.length - 1].critValCritD)
        .format(DAY_OF_MONTH)

      // 開始日の前に null を挿入
      dataChartLineOneYearDaily.push([oneDayBeforeTenYear, null])
      dataChartColumnOneYearDaily.push([oneDayBeforeTenYear, null])

      for (let i = oneYearBefore; i < startDate; i = Moment(i).add(1, 'day').format(DAY_OF_MONTH)) {
        const day = (new Date(i)).getUTCDay()
        if (day !== 0 && day !== 6) {
          dataChartLineOneYearDaily.push([i, null])
          dataChartColumnOneYearDaily.push([i, null])
        }
      }

      // プッシュデータチャートとフォーマット
      investTrustProductOneYear.forEach((item) => {
        if (item.critValCritD >= oneYearBefore && item.critValCritD <= oneDayBefore) {
          dataChartLineOneYearDaily.push([
            item.critValCritD,
            CommonUtil.roundNumber(item.critVal, FormatConstants.RATIO_WITHOUT_UNIT)
          ])
          dataChartColumnOneYearDaily.push([
            item.critValCritD,
            CommonUtil.roundNumber(item.asset, FormatConstants.INVEST_WITHOUT_UNIT)
          ])
        }
      })

      // 終了日の後に null を挿入
      if (endDate < oneDayBefore) {
        for (let i = endDate; i <= oneDayBefore; i = Moment(i).add(1, 'day').format(DAY_OF_MONTH)) {
          const day = (new Date(i)).getUTCDay()
          if ((day === 0 || day === 6) && i !== oneDayBefore) {
            continue
          }
          dataChartLineOneYearDaily.push([i, null])
          dataChartColumnOneYearDaily.push([i, null])
        }
      }
    } else {
      for (let i = oneYearBefore; i <= oneDayBefore; i = Moment(i).add(1, 'day').format(DAY_OF_MONTH)) {
        dataChartLineOneYearDaily.push([i, null])
        dataChartColumnOneYearDaily.push([i, null])
      }
    }

    // 形式のタイムスタンプを変換する
    dataChartLineOneYearDaily.forEach((item) => {
      item[0] = Date.parse(item[0])
    })
    dataChartColumnOneYearDaily.forEach((item) => {
      item[0] = Date.parse(item[0])
    })

    return [dataChartLineOneYearDaily, dataChartColumnOneYearDaily]
  }

  /**
   * 10年場合の折れ線チャートのデータフォーマット
   * @param cacg1080ViewModel「運用商品詳細（投資信託）：チャート」（CACG1080）画面のビューモデル
   * @returns 10年場合の折れ線チャート表示の返却データ
   */
  public formatDataChartTenYear(
    cacg1080ViewModel: CACG1080ViewModel
  ): Array<any> {
    const dataChartLineTenYearMonthly = []
    const dataChartColumnTenYearMonthly = []
    if (
      CommonUtil.isNotEmpty(
        cacg1080ViewModel.investTrustProductTenYearMonthlyLst
      )
    ) {
      const investTrustProductTenYear =
        cacg1080ViewModel.investTrustProductTenYearMonthlyLst.reverse()

      const currentDate = Moment().subtract(1, 'month').format(LAST_OF_MONTH)
      const tenYearBefore = Moment().subtract(11, 'year').format(DAY_OF_MONTH)
      const startDate = Moment(investTrustProductTenYear[0].critValCritYM).format(DAY_OF_MONTH)
      const endDate = Moment(investTrustProductTenYear[investTrustProductTenYear.length - 1].critValCritYM).format(DAY_OF_MONTH)

      for (let i = tenYearBefore; i < startDate; i = Moment(i).add(1, 'day').format(DAY_OF_MONTH)) {
        dataChartLineTenYearMonthly.push([i, null])
        dataChartColumnTenYearMonthly.push([i, null])
      }

      let index = 0
      for (let i = startDate; i <= endDate; i = Moment(i).add(1, 'day').format(DAY_OF_MONTH)) {
        if (i < currentDate) {
          if (Moment(investTrustProductTenYear[index].critValCritYM).format(FIRST_DAY_OF_MONTH) === i) {
            dataChartLineTenYearMonthly.push([
              i,
              CommonUtil.roundNumber(
                investTrustProductTenYear[index]
                  .critVal,
                FormatConstants.RATIO_WITHOUT_UNIT
              )
            ])
            dataChartColumnTenYearMonthly.push([
              i,
              CommonUtil.roundNumber(
                investTrustProductTenYear[index]
                  .asset,
                FormatConstants.INVEST_WITHOUT_UNIT
              )
            ])
            index = index < 119 ? index + 1 : index
          } else {
            dataChartLineTenYearMonthly.push([i, null])
            dataChartColumnTenYearMonthly.push([i, null])
          }
        } else {
          break
        }
      }

      for (let i = endDate; i <= currentDate; i = Moment(i).add(1, 'day').format(DAY_OF_MONTH)) {
        dataChartLineTenYearMonthly.push([i, null])
        dataChartColumnTenYearMonthly.push([i, null])
      }
    }

    dataChartLineTenYearMonthly.forEach((item) => {
      item[0] = Date.parse(item[0])
    })
    dataChartColumnTenYearMonthly.forEach((item) => {
      item[0] = Date.parse(item[0])
    })

    return [dataChartLineTenYearMonthly, dataChartColumnTenYearMonthly]
  }
}
